/**
 * The session: what tokens are held, and how they are renewed.
 *
 * A copy of the staff application's `src/web/src/lib/session.ts`, differing in exactly
 * one place - the configuration route, marked below. Copied rather than shared because
 * the two applications are built and deployed separately and neither may hold up the
 * other's release; kept otherwise byte-identical so that a diff between them shows the
 * drift. A fix to the refresh single-flight below belongs in both.
 *
 * Everything is in memory. Nothing is written to `localStorage`, which is the ARMS
 * guidance and, here, also the only workable answer: the application is served from
 * codemax-app.com and ARMS lives on c-mx.co, so a cookie between them would be
 * cross-site and Safari would refuse it.
 *
 * A reload therefore starts with no tokens, and `restore` gets them back with a silent
 * top-level redirect rather than by having stored anything.
 */

import {
  ArmsError,
  authorizeUrl,
  createPkce,
  exchangeCode,
  exchangeRefreshToken,
  logoutUrl,
  randomString,
  readClaims,
  rememberPending,
  takePending,
  type ArmsConfig,
  type ArmsTokens,
} from './arms'
import { BASE } from './base'

let config: ArmsConfig | undefined
let tokens: ArmsTokens | undefined
let refreshInFlight: Promise<boolean> | null = null

/** Renew this long before expiry, so a request in flight never carries a dead token. */
const RENEW_MARGIN_MS = 60_000

/** Set across the ARMS end-session redirect, and read once on the way back. */
const SWITCH_KEY = 'quotehero.arms.switch'

export async function loadConfig(): Promise<ArmsConfig> {
  if (config) return config

  // The portal's own route, and the only line in this file that differs from the staff
  // application's copy of it. Same ARMS, same audience, a different public client -
  // because a redirect URI is registered against a client, and this is another origin.
  const response = await fetch(`${BASE}/meta/portal-auth-config`, {
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error('The API did not say which Codemax sign-in to use.')

  config = (await response.json()) as ArmsConfig
  return config
}

export function getAccessToken(): string | undefined {
  return tokens?.accessToken
}

export function getIdToken(): string | undefined {
  return tokens?.idToken
}

export function hasSession(): boolean {
  return tokens !== undefined
}

export function clearSession(): void {
  tokens = undefined
}

/**
 * The routes that exist only to get somebody signed in. None of them is a place to
 * come back to: the button is on /sign-in, so without this every sign-in would ask to
 * return to the sign-in screen.
 */
const TRANSIENT_PATHS = new Set(['/sign-in', '/signed-out', '/auth/callback'])

/** Where to send the browser back to once sign-in finishes. */
function currentLocation(): string {
  if (TRANSIENT_PATHS.has(window.location.pathname)) return '/'

  return window.location.pathname + window.location.search
}

async function redirectToArms(silent: boolean, returnTo: string): Promise<void> {
  const settings = await loadConfig()
  const { verifier, challenge } = await createPkce()
  const state = randomString(16)
  const nonce = randomString(16)

  rememberPending({ verifier, state, nonce, returnTo })

  window.location.assign(authorizeUrl(settings, { challenge, state, nonce, silent }))
}

/** Sends the browser to the ARMS sign-in page. Does not return. */
export function signIn(returnTo = currentLocation()): Promise<void> {
  return redirectToArms(false, returnTo)
}

/**
 * Tries to pick the session back up without showing anybody a sign-in page.
 *
 * Returns false when ARMS says interaction is required, which is simply what a first
 * visit looks like. Because this is a redirect the successful path never returns - the
 * browser leaves and comes back at /auth/callback.
 */
export async function restore(): Promise<boolean> {
  try {
    await loadConfig()
  } catch {
    return false
  }

  // A silent attempt is only worth making once per load. Without this guard a tenant
  // that always answers login_required would bounce the browser forever.
  if (sessionStorage.getItem('quotehero.arms.silentTried') === '1') return false

  try {
    sessionStorage.setItem('quotehero.arms.silentTried', '1')
  } catch {
    return false
  }

  await redirectToArms(true, currentLocation())
  return true
}

export interface CallbackResult {
  readonly returnTo: string
}

/**
 * Completes the redirect. Throws {@link ArmsError} if ARMS refused, and the caller can
 * ask whether that refusal simply means nobody is signed in.
 */
export async function completeCallback(search: string): Promise<CallbackResult> {
  const query = new URLSearchParams(search)
  const pending = takePending()

  const error = query.get('error')
  if (error) {
    throw new ArmsError(error, query.get('error_description') ?? 'Codemax refused the sign-in.')
  }

  const code = query.get('code')
  if (!code || !pending) {
    throw new ArmsError('invalid_request', 'That sign-in link is no longer valid. Try again.')
  }

  // The state is the CSRF defence: it proves this redirect answers the request this tab
  // made, rather than one an attacker made in it.
  if (query.get('state') !== pending.state) {
    throw new ArmsError('invalid_state', 'That sign-in did not start from this tab. Try again.')
  }

  const settings = await loadConfig()
  const issued = await exchangeCode(settings, code, pending.verifier)

  const nonce = readClaims(issued.idToken).nonce
  if (issued.idToken && nonce !== pending.nonce) {
    throw new ArmsError('invalid_nonce', 'That sign-in could not be verified. Try again.')
  }

  tokens = issued
  sessionStorage.removeItem('quotehero.arms.silentTried')

  return { returnTo: pending.returnTo || '/' }
}

/**
 * Renews the access token, once, however many callers ask at the same time.
 *
 * The single flight is not an optimisation. ARMS refresh tokens are single-use and
 * concurrent refreshes invalidate each other, so ten parallel 401s firing ten refreshes
 * would leave nine replays and a dead session.
 */
export function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  const token = tokens?.refreshToken
  if (!token || !config) return Promise.resolve(false)

  const settings = config

  refreshInFlight = (async () => {
    try {
      tokens = await exchangeRefreshToken(settings, token)
      return true
    } catch (cause) {
      // A refused refresh is a finished session; a network failure is not. Only the
      // first should drop what we hold.
      if (cause instanceof ArmsError) clearSession()
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

/** True when the access token is close enough to expiry to be worth renewing now. */
export function needsRenewal(): boolean {
  return tokens !== undefined && tokens.expiresAt - Date.now() < RENEW_MARGIN_MS
}

/**
 * Ends the session here and at ARMS.
 *
 * Both are needed: signing out of ARMS does not clear this application, and clearing
 * this application does not end the ARMS session - so doing only one leaves the next
 * visitor to this browser signed in.
 */
export async function signOut(): Promise<void> {
  return endSession()
}

/**
 * Ends the ARMS session and comes back ready to sign in as somebody else.
 *
 * The same end-session redirect as {@link signOut} - there is no other way to drop an
 * ARMS session, and without dropping it the next authorize request is answered from the
 * cookie with the account that was just refused. The marker is what turns the landing
 * page into a sign-in rather than a full stop, so switching account is one press rather
 * than three.
 */
export function switchAccount(): Promise<void> {
  try {
    sessionStorage.setItem(SWITCH_KEY, '1')
  } catch {
    // Storage refused: the marker is lost and the sign-out screen shows its button.
    // One press more, and nothing broken.
  }

  return endSession()
}

/**
 * Whether the sign-out being landed on was a request to change account. Reads once and
 * clears, so a later reload of the same page does not send the browser off again.
 */
export function takeSwitchRequest(): boolean {
  try {
    const wanted = sessionStorage.getItem(SWITCH_KEY) === '1'
    sessionStorage.removeItem(SWITCH_KEY)
    return wanted
  } catch {
    return false
  }
}

async function endSession(): Promise<void> {
  const settings = config ?? (await loadConfig().catch(() => undefined))
  const idToken = tokens?.idToken

  clearSession()

  try {
    sessionStorage.removeItem('quotehero.arms.silentTried')
  } catch {
    /* nothing stored, nothing to remove */
  }

  if (settings) {
    window.location.assign(logoutUrl(settings, idToken))
  } else {
    window.location.assign('/signed-out')
  }
}
